import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  RuleType,
  RuleConfig,
  Rule,
  TouchModel,
  JourneyConfig,
  AffinityConfig,
  CheckoutConfig,
  FallbackConfig,
} from '../types';
import { RULE_TYPE_LABELS, RULE_TYPE_DESCRIPTIONS } from '../types';

const RULE_TYPE_OPTIONS: RuleType[] = ['journey', 'affinity', 'checkout', 'fallback'];

interface CreateRuleModalProps {
  open: boolean;
  rule?: Rule | null;
  saving: boolean;
  onClose: () => void;
  onSave: (data: { name: string; type: RuleType; config: RuleConfig }) => void;
}

export function CreateRuleModal({ open, rule, saving, onClose, onSave }: CreateRuleModalProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<RuleType>('journey');
  const [journeyConfig, setJourneyConfig] = useState<JourneyConfig>({ touchModel: 'firstTouch', paramName: 'pws' });
  const [affinityConfig, setAffinityConfig] = useState<AffinityConfig>({ metafieldNamespace: 'custom', metafieldKey: 'sponsor' });
  const [checkoutConfig, setCheckoutConfig] = useState<CheckoutConfig>({ attributeKey: 'distributor_id' });
  const [fallbackConfig, setFallbackConfig] = useState<FallbackConfig>({ houseAccount: '' });

  useEffect(() => {
    if (rule) {
      setName(rule.name);
      setType(rule.type);

      switch (rule.type) {
        case 'journey':
          setJourneyConfig({ touchModel: 'firstTouch', paramName: 'pws', ...(rule.config as Partial<JourneyConfig>) });
          break;
        case 'affinity':
          setAffinityConfig({ metafieldNamespace: 'custom', metafieldKey: 'sponsor', ...(rule.config as Partial<AffinityConfig>) });
          break;
        case 'checkout':
          setCheckoutConfig({ attributeKey: 'distributor_id', ...(rule.config as Partial<CheckoutConfig>) });
          break;
        case 'fallback':
          setFallbackConfig({ houseAccount: '', ...(rule.config as Partial<FallbackConfig>) });
          break;
      }
    } else {
      setName('');
      setType('journey');
      setJourneyConfig({ touchModel: 'firstTouch', paramName: 'pws' });
      setAffinityConfig({ metafieldNamespace: 'custom', metafieldKey: 'sponsor' });
      setCheckoutConfig({ attributeKey: 'distributor_id' });
      setFallbackConfig({ houseAccount: '' });
    }
  }, [rule, open]);

  const getCurrentConfig = (): RuleConfig => {
    switch (type) {
      case 'journey':
        return journeyConfig;
      case 'affinity':
        return affinityConfig;
      case 'checkout':
        return checkoutConfig;
      case 'fallback':
        return fallbackConfig;
    }
  };

  const handleSave = () => {
    onSave({ name, type, config: getCurrentConfig() });
  };

  const isValid = (): boolean => {
    if (!name.trim()) return false;

    switch (type) {
      case 'journey':
        return !!(journeyConfig.paramName?.trim());
      case 'affinity':
        return !!(affinityConfig.metafieldNamespace?.trim()) && !!(affinityConfig.metafieldKey?.trim());
      case 'checkout':
        return !!(checkoutConfig.attributeKey?.trim());
      case 'fallback':
        return !!(fallbackConfig.houseAccount?.trim());
    }
  };

  const renderTypeConfig = () => {
    switch (type) {
      case 'journey':
        return (
          <div className="space-y-4">
            <div className="space-y-3">
              <Label>Touch Model</Label>
              <RadioGroup
                value={journeyConfig.touchModel}
                onValueChange={(v) => setJourneyConfig({ ...journeyConfig, touchModel: v as TouchModel })}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="firstTouch" id="firstTouch" />
                  <Label htmlFor="firstTouch" className="font-normal cursor-pointer">First Touch</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="lastTouch" id="lastTouch" />
                  <Label htmlFor="lastTouch" className="font-normal cursor-pointer">Last Touch</Label>
                </div>
              </RadioGroup>
              <p className="text-xs text-muted-foreground">
                Choose whether to attribute to the first or last touchpoint in the customer journey
              </p>
            </div>
            <div className="space-y-2">
              <Label>URL Parameter Name</Label>
              <Input
                value={journeyConfig.paramName}
                onChange={(e) => setJourneyConfig({ ...journeyConfig, paramName: e.target.value })}
                placeholder="e.g., pws, sponsor, distributor"
              />
              <p className="text-xs text-muted-foreground">
                The URL parameter to extract distributor site from (e.g., ?pws=marysmith)
              </p>
            </div>
          </div>
        );

      case 'affinity':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Metafield Namespace</Label>
              <Input
                value={affinityConfig.metafieldNamespace}
                onChange={(e) => setAffinityConfig({ ...affinityConfig, metafieldNamespace: e.target.value })}
                placeholder="e.g., custom"
              />
              <p className="text-xs text-muted-foreground">
                The Shopify customer metafield namespace
              </p>
            </div>
            <div className="space-y-2">
              <Label>Metafield Key</Label>
              <Input
                value={affinityConfig.metafieldKey}
                onChange={(e) => setAffinityConfig({ ...affinityConfig, metafieldKey: e.target.value })}
                placeholder="e.g., sponsor"
              />
              <p className="text-xs text-muted-foreground">
                The metafield key containing the distributor ID
              </p>
            </div>
          </div>
        );

      case 'checkout':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Cart Attribute Key</Label>
              <Input
                value={checkoutConfig.attributeKey}
                onChange={(e) => setCheckoutConfig({ ...checkoutConfig, attributeKey: e.target.value })}
                placeholder="e.g., distributor_id"
              />
              <p className="text-xs text-muted-foreground">
                The cart attribute key containing the distributor ID
              </p>
            </div>
          </div>
        );

      case 'fallback':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>House Account</Label>
              <Input
                value={fallbackConfig.houseAccount}
                onChange={(e) => setFallbackConfig({ ...fallbackConfig, houseAccount: e.target.value })}
                placeholder="e.g., CORPORATE, HOUSE"
              />
              <p className="text-xs text-muted-foreground">
                The default distributor ID/code to assign when no other rules match
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{rule ? 'Edit Attribution Rule' : 'Create Attribution Rule'}</DialogTitle>
          <DialogDescription>
            Configure how orders are attributed to distributors
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label>Rule Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., First Touch URL Param"
            />
          </div>

          <div className="space-y-2">
            <Label>Rule Type</Label>
            <Select
              value={type}
              onValueChange={(v) => setType(v as RuleType)}
              disabled={!!rule}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RULE_TYPE_OPTIONS.map((ruleType) => (
                  <SelectItem key={ruleType} value={ruleType}>
                    {RULE_TYPE_LABELS[ruleType]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{RULE_TYPE_DESCRIPTIONS[type]}</p>
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-4">Configuration</h4>
            {renderTypeConfig()}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid() || saving}>
            {saving ? 'Saving...' : rule ? 'Save Changes' : 'Create Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
